/**
 * Shared helpers for inspection signature images and location text (client + admin).
 */

function getStoredAuthToken() {
  if (typeof window === 'undefined') return '';
  return (
    localStorage.getItem('admin_token') ||
    localStorage.getItem('session_token') ||
    localStorage.getItem('token') ||
    ''
  );
}

function resolveBackendOrigin() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

/** Append session/JWT token so <img src> can load protected /api/inspections/* URLs. */
export function withInspectionMediaAuth(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('token=')) return url;
  const needsAuth =
    url.includes('/api/inspections/') || url.includes('/api/photos/');
  if (!needsAuth) return url;
  const token = getStoredAuthToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

export function normalizeMediaDataUrl(raw) {
  if (raw == null) return '';
  if (typeof raw === 'object') {
    raw = raw.url || raw.data_url || raw.signature_image || raw.data || raw.photo_data || raw.base64;
  }
  if (typeof raw !== 'string') return '';
  let u = raw.trim();
  if (!u) return '';
  if (u.startsWith('/api/')) {
    const base = resolveBackendOrigin();
    if (base) u = `${base}${u}`;
    return withInspectionMediaAuth(u);
  }
  if (u.startsWith('data:image') || u.startsWith('http://') || u.startsWith('https://')) {
    if (u.startsWith('data:image/jpeg;base64,') && u.includes('iVBORw0KGgo')) {
      return u.replace('data:image/jpeg;base64,', 'data:image/png;base64,');
    }
    return withInspectionMediaAuth(u);
  }
  const compact = u.replace(/\s/g, '');
  if (compact.length > 40 && /^[A-Za-z0-9+/=]+$/.test(compact.slice(0, 500))) {
    const mime = compact.startsWith('iVBORw0KGgo') ? 'image/png' : 'image/jpeg';
    return `data:${mime};base64,${compact}`;
  }
  return '';
}

export function getInspectionSignatureSources(inspection) {
  if (!inspection || typeof inspection !== 'object') {
    return { imageUrl: '', signerName: '' };
  }
  const sig = inspection.signature;
  if (sig && typeof sig === 'object') {
    const imageUrl = normalizeMediaDataUrl(sig.data_url || sig.image || sig.url);
    const signerName = (sig.signer_name || inspection.signed_by || '').trim();
    if (imageUrl || signerName) return { imageUrl, signerName };
  }
  const flat = normalizeMediaDataUrl(inspection.signature_image);
  if (flat) {
    return {
      imageUrl: flat,
      signerName: (inspection.signed_by || '').trim(),
    };
  }
  const cs = inspection.client_signature;
  if (cs && typeof cs === 'object') {
    return {
      imageUrl: normalizeMediaDataUrl(cs.signature_image),
      signerName: (cs.signer_name || inspection.signed_by || '').trim(),
    };
  }
  return { imageUrl: '', signerName: (inspection.signed_by || '').trim() };
}

function coordPair(loc) {
  if (!loc || typeof loc !== 'object') return null;
  const lat = loc.latitude ?? loc.lat;
  const lng = loc.longitude ?? loc.lng ?? loc.lon;
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const la = Number(lat);
  const ln = Number(lng);
  if (Number.isFinite(la) && Number.isFinite(ln)) return { lat: la, lng: ln };
  return null;
}

export function getInspectionLocationDisplay(inspection, unavailableLabel = '') {
  if (!inspection || typeof inspection !== 'object') {
    return unavailableLabel;
  }
  const loc = inspection.location;
  if (loc && typeof loc === 'object') {
    const addr = (loc.address || loc.display_address || '').trim();
    if (addr) return addr;
    const coords = coordPair(loc);
    if (coords) return `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
  }
  const info = inspection.service_info;
  const itype = (inspection.inspection_type || 'pickup').toLowerCase();
  if (info && typeof info === 'object') {
    const pick =
      (info.pickup_location && info.pickup_location.address) ||
      (info.pickup_address || '').trim();
    const del =
      (info.delivery_location && info.delivery_location.address) ||
      (info.delivery_address || '').trim();
    if (itype === 'delivery' && del) return del;
    if (itype !== 'delivery' && pick) return pick;
    if (del) return del;
    if (pick) return pick;
    const cur = info.current_location;
    if (cur && typeof cur === 'object') {
      const curAddr = (cur.address || '').trim();
      if (curAddr) return curAddr;
      const c = coordPair(cur);
      if (c) return `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
    }
  }
  return unavailableLabel;
}

export function getInspectionMapsUrl(inspection) {
  const loc = inspection?.location;
  if (loc?.maps_url) return loc.maps_url;
  const c = coordPair(loc);
  if (c) return `https://www.google.com/maps?q=${c.lat},${c.lng}`;
  return null;
}

function getAiCore(inspection) {
  const ai = inspection?.ai_analysis;
  if (!ai || typeof ai !== 'object') return null;
  if (ai.analysis && typeof ai.analysis === 'object') return ai.analysis;
  return ai;
}

/** False when AI determined photos are not of a vehicle (defaults true if unknown). */
export function isInspectionVehicleDetected(inspection) {
  if (!inspection || typeof inspection !== 'object') return true;
  const summary = inspection.ai_summary;
  if (summary && typeof summary.vehicle_detected === 'boolean') {
    return summary.vehicle_detected;
  }
  const core = getAiCore(inspection);
  if (core && typeof core.is_vehicle === 'boolean') {
    return core.is_vehicle;
  }
  return true;
}

/** Driver verification selfie (admin-only); string or { url, taken_at, ... }. */
export function getInspectionDriverSelfieSources(inspection) {
  if (!inspection || typeof inspection !== 'object') {
    return { imageUrl: '', takenAt: '' };
  }
  if (inspection.driver_selfie_url) {
    return {
      imageUrl: normalizeMediaDataUrl(inspection.driver_selfie_url),
      takenAt: (inspection.driver_selfie_taken_at || '').trim(),
    };
  }
  const raw = inspection.driver_selfie;
  if (typeof raw === 'string') {
    return { imageUrl: normalizeMediaDataUrl(raw), takenAt: '' };
  }
  if (raw && typeof raw === 'object') {
    return {
      imageUrl: normalizeMediaDataUrl(
        raw.url || raw.data_url || raw.data || raw.photo_data || raw.base64,
      ),
      takenAt: (raw.taken_at || raw.uploaded_at || '').trim(),
    };
  }
  return { imageUrl: '', takenAt: (inspection.driver_selfie_taken_at || '').trim() };
}

export function inspectionHasDriverSelfie(inspection) {
  if (!inspection || typeof inspection !== 'object') return false;
  if (inspection.has_driver_selfie) return true;
  return Boolean(getInspectionDriverSelfieSources(inspection).imageUrl);
}

/** List/detail APIs may send photo_count without inline photo URLs (lazy-load endpoints). */
export function getInspectionPhotoCount(inspection) {
  if (typeof inspection?.photo_count === 'number' && inspection.photo_count >= 0) {
    return inspection.photo_count;
  }
  return normalizeInspectionPhotos(inspection).length;
}

export function normalizeInspectionPhotos(inspection) {
  const toPhotoObject = (photo, index) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      return { url: photo, type: `photo_${index + 1}` };
    }
    if (typeof photo === 'object') {
      const url = photo.url || photo.data_url || photo.data || photo.photo_data || photo.base64;
      if (!url) return null;
      return { ...photo, url, type: photo.type || photo.photo_type || `photo_${index + 1}` };
    }
    return null;
  };

  if (Array.isArray(inspection?.photos) && inspection.photos.length > 0) {
    const normalized = inspection.photos.map(toPhotoObject).filter(Boolean);
    if (normalized.length > 0) return normalized;
  }

  const images = inspection?.images;
  if (Array.isArray(images) && images.length > 0) {
    return images.map(toPhotoObject).filter(Boolean);
  }

  if (typeof images === 'string' && images.trim()) {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) {
        return parsed.map(toPhotoObject).filter(Boolean);
      }
    } catch (_) {
      return [];
    }
  }

  const count =
    typeof inspection?.photo_count === 'number' && inspection.photo_count >= 0
      ? inspection.photo_count
      : 0;
  const inspectionId = inspection?.id;
  if (count > 0 && inspectionId) {
    return Array.from({ length: count }, (_, index) => ({
      url: `/api/inspections/${inspectionId}/photos/${index}`,
      type: `photo_${index + 1}`,
    }));
  }

  return [];
}

export function getInspectionVehicleValidationMessage(inspection) {
  if (!inspection || typeof inspection !== 'object') return '';
  const summary = inspection.ai_summary;
  if (summary && typeof summary.validation_message === 'string') {
    const msg = summary.validation_message.trim();
    if (msg) return msg;
  }
  const core = getAiCore(inspection);
  if (core) {
    const desc = (core.non_vehicle_description || '').trim();
    if (desc) return desc;
    const notes = (core.notes || '').trim();
    if (notes && core.is_vehicle === false) return notes;
  }
  return '';
}
